import { getProductConfig } from '@/app/config/products.config';
import {
  AnalysisTabsCard,
  type AnalysisTabItem,
} from '@/features/analyses/components';
import {
  PmeAnalysisCommercializationSection,
  PmeAnalysisVersionsSection,
} from '@/features/analyses/pme/components/pme-analysis-sections.component';
import type { PmeAnalysisCommercialization } from '@/features/analyses/pme/pme-analysis.mock';
import { PmeLivesByStipulantSection } from '@/features/proposals/components/pme/steps/sections/lives-by-stipulant-section.component';
import { mapPremiumValues } from '@/features/proposals/components/pme/utils/hydrate-proposal.util';
import {
  useGroupLifeProposalVersions,
  useSaveGroupLifeProposalCommercialization,
} from '@/features/proposals/hooks/group-life';
import {
  CalculationStipulantsListSection,
  CalculationSummaryCardSection,
} from '@/features/quotes/components/capital-global/steps/sections/step-calculation.sections';
import { RecalculationProgressOverlay } from '@/features/quotes/components/quote-steps/group-life/recalculation-progress-overlay.component';
import { useMembersRatingProgress } from '@/features/quotes/hooks/group-life/use-members-rating-progress.hook';
import { parsePercent } from '@/features/quotes/schemas/commercialization-percent.schema';
import { type GroupLifeProposalVersionItem } from '@/services/proposals/group-life';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { useIsUnderwriterUser } from '@/shared/hooks';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { Spinner, Tag, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import {
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  Home,
  Info,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { PmeProposalAnalysisActions } from './components/pme-proposal-analysis-actions.component';
import {
  PmeAcceptanceCriteriaTab,
  PmeAfastadosTab,
  PmeAgeCapitalTab,
  PmeCoveragesServicesTab,
} from './components/pme-proposal-analysis-tab-contents.component';
import {
  PME_PROPOSAL_ANALYSIS_LIVES_PAGE_SIZE,
  usePmeProposalAnalysis,
} from './hooks/use-pme-proposal-analysis.hook';

export type PmeProposalAnalysisTab = 'resume' | 'observations' | 'interactions';

type Props = {
  /** Id da proposta PME em análise (rota `/proposal-analyses/$proposalId`). */
  proposalId: string;
  /** Mantido por compatibilidade com o resolver da rota; não utilizado aqui. */
  initialTab?: PmeProposalAnalysisTab;
};

const ICON_CLASS = 'h-4 w-4';

function toDisplayPercent(value?: string): string {
  const normalized = value?.trim() ?? '';
  if (!normalized) return '0%';
  return normalized.endsWith('%') ? normalized : `${normalized}%`;
}

function toCommercializationState(commercialization?: {
  brokeragePercent?: string;
  agencingPercent?: string;
  agencyPercent?: string;
  surchargePercent?: string;
  actuarialDiscountPercent?: string;
}): PmeAnalysisCommercialization {
  return {
    brokeragePercent: toDisplayPercent(commercialization?.brokeragePercent),
    agencyPercent: toDisplayPercent(
      commercialization?.agencingPercent || commercialization?.agencyPercent
    ),
    surcharge: toDisplayPercent(commercialization?.surchargePercent),
    actuarialDiscount: toDisplayPercent(
      commercialization?.actuarialDiscountPercent
    ),
  };
}

function PmeProposalAnalysisPremiumCard({
  totalValue,
  liquidValue,
  iofValue,
  paymentFrequency,
  contractType,
  proposalNumber,
}: {
  totalValue: string;
  liquidValue: string;
  iofValue: string;
  paymentFrequency: string;
  contractType: string;
  proposalNumber: string;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className='relative w-full rounded-2xl bg-linear-to-br from-[#2C285F] to-[#18163D] px-4 py-3 text-alba-white-500 sm:px-6'>
      <button
        type='button'
        onClick={() => setExpanded(value => !value)}
        className='absolute right-3 top-2 flex h-8 w-8 items-center justify-center'
        aria-label={expanded ? 'Recolher prêmio' : 'Expandir prêmio'}
      >
        {expanded ? (
          <ChevronUp className='h-4 w-4' />
        ) : (
          <ChevronDown className='h-4 w-4' />
        )}
      </button>

      <div className='flex flex-col items-center'>
        <Text className='text-xs text-alba-white-500'>Prêmio Total</Text>
        <Text className='text-2xl font-bold text-alba-white-500'>
          {totalValue}
        </Text>
      </div>

      {expanded && (
        <>
          <div className='mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3'>
            <PremiumDetail label='Prêmio Líquido' value={liquidValue} />
            <PremiumDetail label='IOF' value={iofValue} />
            <PremiumDetail
              label='Periodicidade de pagamento'
              value={paymentFrequency}
            />
          </div>
          <div className='my-3 h-px bg-alba-gray-400/70' />
          <div className='grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4'>
            <PremiumDetail label='Produto' value='PME' />
            <PremiumDetail label='Tipo de Contratação' value={contractType} />
            <PremiumDetail label='Número da Cotação' value={proposalNumber} />
            <PremiumDetail label='Status' value='Em Análise' />
          </div>
        </>
      )}
    </div>
  );
}

function PremiumDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex flex-col gap-0.5'>
      <Text className='text-[10px] text-alba-white-500'>{label}</Text>
      <Text className='text-xs font-bold text-alba-white-500'>{value}</Text>
    </div>
  );
}

/**
 * Página "Análise Proposta PME". Segue o layout da análise de cotação PME
 * (prêmio, abas Critérios/Coberturas/Idade/Afastados, Comercialização, Dados da
 * Cotação e Lista de Estipulantes) alimentado por dados reais do Group Life. As
 * ações (Tarifação, Recusar, Aprovar) usam os endpoints de análise de proposta.
 */
export function PmeProposalAnalysisPage({ proposalId }: Props) {
  const navigate = useNavigate();
  const { isUnderwriterUser } = useIsUnderwriterUser();
  const {
    details,
    isLoading,
    error,
    insuranceCard,
    stipulantCard,
    accordions,
    coverageRows,
    serviceRows,
    ageCapitalRows,
    afastadoRows,
    isLoadingLifeCapital,
    criteria,
  } = usePmeProposalAnalysis(proposalId);
  const policyId = details?.policyId ?? proposalId;
  const isCancelled =
    Number(details?.statusPortal) === PortalStatus.ProposalCancelled;
  const versionsQuery = useGroupLifeProposalVersions(policyId);
  const versions: GroupLifeProposalVersionItem[] =
    versionsQuery.data?.versions ?? [];
  // O prêmio exibido vem do snapshot VIVO da proposta: o recálculo da análise
  // grava o prêmio na própria apólice (calculateEx + updateEx) e o save já
  // devolve o detalhe atualizado. Ler de uma versão exigiria criar uma a cada
  // save — custo que não se paga, já que só a ProposalVersion_0 tem uso
  // funcional (restaurada no Recusar).
  const displayedPremium = useMemo(
    () => mapPremiumValues(details?.calculation),
    [details?.calculation]
  );
  const saveCommercialization = useSaveGroupLifeProposalCommercialization();
  const ratingProgress = useMembersRatingProgress({
    policyId,
    enabled: saveCommercialization.isPending,
  });
  const [commercialization, setCommercialization] =
    useState<PmeAnalysisCommercialization>(() =>
      toCommercializationState(details?.commercialization)
    );

  useEffect(() => {
    setCommercialization(toCommercializationState(details?.commercialization));
  }, [details?.commercialization]);

  const breadcrumb = (
    <Page.Breadcrumb
      items={[
        {
          label: 'Pagina Inicial',
          href: '/dashboard',
          iconLeft: <Home className='h-4 w-4' />,
        },
        { label: 'Subscrição' },
        { label: 'Propostas em Análise', href: '/proposal-analyses' },
        { label: 'Análise PME', active: true },
      ]}
    />
  );

  if (isLoading) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content>
          <div className='flex w-full items-center justify-center p-8'>
            <Spinner size='lg' colorScheme='blue' />
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  if (error || !details) {
    return (
      <Page.Root>
        {breadcrumb}
        <Page.Content className='py-4'>
          <Text className='text-sm text-alba-error-red-500'>
            Não foi possível carregar a proposta PME. Tente novamente.
          </Text>
        </Page.Content>
      </Page.Root>
    );
  }

  const handleSaveCommercialization = async (): Promise<boolean> => {
    if (saveCommercialization.isPending) return false;

    const brokerage = parsePercent(commercialization.brokeragePercent);
    const agencying = parsePercent(commercialization.agencyPercent);

    try {
      // Os campos de subscrição só são editáveis por subscritores, mas seguem
      // no payload para todos: o estado espelha o que está persistido, e a
      // mutation confirma a persistência comparando os quatro percentuais.
      const result = await saveCommercialization.mutateAsync({
        policyId,
        proposalId,
        payload: {
          brokeragePercent: brokerage,
          agencyingPercent: agencying,
          surchargePercent: parsePercent(commercialization.surcharge),
          actuarialDiscountPercent: parsePercent(
            commercialization.actuarialDiscount
          ),
        },
      });
      toaster.create({
        type: 'success',
        title:
          result.outcome === 'unchanged'
            ? 'Nenhuma alteração a aplicar'
            : 'Proposta recalculada',
        description:
          result.outcome === 'unchanged'
            ? 'Os percentuais informados já estavam salvos na proposta.'
            : 'As alterações de comercialização foram aplicadas na proposta.',
      });
      return true;
    } catch (calculationError) {
      // Nome distinto do `error` da própria página (usePmeProposalAnalysis),
      // que continua em uso mais acima e seria sombreado aqui.
      toaster.create({
        type: 'error',
        title: 'Erro ao recalcular',
        description:
          calculationError instanceof Error
            ? calculationError.message
            : 'Não foi possível recalcular a proposta. Tente novamente.',
      });
      return false;
    }
  };

  const openVersionSummary = () => {
    void navigate({
      to: '/proposals/$id/pme',
      params: { id: policyId },
    });
  };

  const tabs: AnalysisTabItem[] = [
    {
      id: 'criteria',
      label: 'Critérios de Aceitação',
      icon: <Info className={ICON_CLASS} />,
      content: <PmeAcceptanceCriteriaTab criteria={criteria} />,
    },
    {
      id: 'coverages',
      label: 'Coberturas e Serviços',
      icon: <Clock className={ICON_CLASS} />,
      content: (
        <PmeCoveragesServicesTab
          coverages={coverageRows}
          services={serviceRows}
        />
      ),
    },
    {
      id: 'age-capital',
      label: 'Idade / Capital',
      icon: <Users className={ICON_CLASS} />,
      content: (
        <PmeAgeCapitalTab
          rows={ageCapitalRows}
          isLoading={isLoadingLifeCapital}
        />
      ),
    },
    {
      id: 'afastados',
      label: 'Afastados',
      count: afastadoRows.length,
      icon: <Eye className={ICON_CLASS} />,
      content: (
        <PmeAfastadosTab rows={afastadoRows} isLoading={isLoadingLifeCapital} />
      ),
    },
  ];

  return (
    <Page.Root>
      {breadcrumb}

      <PageHeader.Root>
        <PageHeader.Main>
          <PageHeader.TitleGroup>
            <PageHeader.Title>
              Análise Proposta PME - Nº {details.proposalNumber ?? '-'}
            </PageHeader.Title>
            <PageHeader.Subtitle>
              Analise os dados para validação da proposta
            </PageHeader.Subtitle>
          </PageHeader.TitleGroup>
          <PageHeader.Actions>
            <Tag.Root variant='outline' colorScheme='yellow' size='lg'>
              <Tag.Label>Em Análise</Tag.Label>
            </Tag.Root>
          </PageHeader.Actions>
        </PageHeader.Main>
      </PageHeader.Root>

      <Page.Content className='flex flex-col gap-6 py-4'>
        <PmeProposalAnalysisPremiumCard
          totalValue={displayedPremium.totalValue}
          liquidValue={displayedPremium.liquidValue}
          iofValue={displayedPremium.iofValue}
          paymentFrequency={displayedPremium.frequency}
          contractType={
            insuranceCard.items.find(item => item.label === 'Tipo de Seguro')
              ?.value ?? '—'
          }
          proposalNumber={details.proposalNumber ?? '-'}
        />

        <AnalysisTabsCard tabs={tabs} />

        {versions.length > 0 && (
          <PmeAnalysisVersionsSection
            versions={versions}
            onOpenVersion={row => {
              // Versões SOURCE pertencem à proposta de ORIGEM (cadeia de
              // clonagem) — abre a origem em vez da proposta atual.
              if (row.origin === 'SOURCE' && row.sourcePolicyId) {
                void navigate({
                  to: '/proposals/$id/pme',
                  params: { id: row.sourcePolicyId },
                });
                return;
              }
              // Versões da fase de COTAÇÃO da mesma apólice abrem o resumo
              // do wizard de cotação hidratado naquela versão (mesma
              // navegação da tela de análise de cotação).
              if (!row.versionName?.startsWith('ProposalVersion_')) {
                const cfg = getProductConfig(details.productCode);
                void navigate({
                  to: '/quotes/products/new/$id',
                  params: { id: policyId },
                  search: {
                    productId: '',
                    category: cfg?.schemaCategory ?? 'GROUP_LIFE_INSURANCE',
                    productCode: details.productCode,
                    stepCode: 'CALCULATION',
                    quoteFlow: 'view',
                    quotationVersionId: String(row.quotationVersionId),
                  },
                });
                return;
              }
              openVersionSummary();
            }}
            onOpenCurrent={() => openVersionSummary()}
            isLoading={versionsQuery.isLoading}
          />
        )}

        {/* Comercialização */}
        <PmeAnalysisCommercializationSection
          value={commercialization}
          onChange={setCommercialization}
          onSave={handleSaveCommercialization}
          showUnderwritingFields={isUnderwriterUser === true}
          readOnly={isCancelled}
        />

        {/* Dados da Cotação */}
        <div className='flex w-full flex-col gap-6 rounded-2xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] sm:p-6'>
          <Text className='text-base text-alba-black-500'>
            Dados da Cotação
          </Text>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <CalculationSummaryCardSection data={insuranceCard} />
            <CalculationSummaryCardSection data={stipulantCard} />
          </div>

          <CalculationStipulantsListSection entries={accordions} />

          {/* O valor guardado é o salário. A coluna multiplica pela quantidade
              de salários do plano, como na análise da cotação. */}
          <PmeLivesByStipulantSection
            details={details}
            policyId={policyId}
            plan={details?.plan}
            multiplySalary={details?.plan?.modality === 'MULTIPLE_SALARY'}
            pageSize={PME_PROPOSAL_ANALYSIS_LIVES_PAGE_SIZE}
          />
        </div>

        <PmeProposalAnalysisActions
          proposalId={proposalId}
          statusPortal={details?.statusPortal}
          readOnly={isCancelled}
        />
      </Page.Content>
      {saveCommercialization.isPending && (
        <RecalculationProgressOverlay
          progress={ratingProgress.progress}
          error={ratingProgress.error}
        />
      )}
    </Page.Root>
  );
}
